import { Env } from '@env';
import { FlashList } from '@shopify/flash-list';
import { Image } from 'expo-image';
import { useLocalSearchParams, useRouter } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { Car } from '@/api/cars/types';
import { useCustomer } from '@/api/customers/use-customer';
import { useConnectStandardFleet } from '@/api/partners/cars/use-connect-standard-fleet';
import { useListedCars } from '@/api/partners/cars/use-listed-cars';
import { useNeedsSetupCarsPaginated } from '@/api/partners/cars/use-needs-setup-cars-paginated';
import { useRelistCar } from '@/api/partners/cars/use-relist-car';
import { useUnlistedCars } from '@/api/partners/cars/use-unlisted-cars';
import { useVerifyVirtualKeys } from '@/api/partners/cars/use-verify-virtual-keys';
import { CarListedModal } from '@/components/cars/car-listed-modal';
import { CarsListedModal } from '@/components/cars/cars-listed-modal';
import { getCarImages, isDesktop, useAuth } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { useDebounce } from '@/core/hooks/use-debounced-value';
import { usePoll } from '@/core/hooks/use-poll';
import { useTrackMetaEvent } from '@/core/hooks/use-track-meta-event';
import { Button, Card, Input, showErrorMessage, Text } from '@/ui';
import Accordion from '@/ui/accordion';
import Alert, { AlertType } from '@/ui/alert';
import { Chip, ChipType } from '@/ui/chip';
import DataTable from '@/ui/data-table';
import { Icon } from '@/ui/icon';
import { Search } from '@/ui/icons';
import { Dot } from '@/ui/icons/dot';
import { Pencil } from '@/ui/icons/pencil';
import { TrashFill } from '@/ui/icons/trash-fill';
import { Loader } from '@/ui/loader';

import reservations from '../reservations';
import { AddCarsModal } from './add-cars-modal';
import { DriverAccountModal } from './driver-account-modal';
import { SetupDigitalKeyModal } from './setup-digital-key-modal';
import { UnlistCarModal } from './unlist-car-modal';

enum Tab {
  LISTED = 'Listed',
  NEEDS_SETUP = 'Needs Setup',
  UNLISTED = 'Unlisted',
}

const UNLISTED_REQUIRED_FIELDS: (keyof Car)[] = [
  'trim',
  'licensePlate',
  'licensePlateState',
  'color',
  'gloveboxPin',
  'defaultLocation',
];

export const MODEL_MINIMUM_RATES: Record<string, number> = {
  'Model 3': 35,
  'Model Y': 40,
  'Model S': 60,
  'Model X': 70,
  Cybertruck: 120,
  Air: 90,
  R1t: 90,
  R1s: 90,
};

function isPricingValid(car: Car): boolean {
  if (car.useAutomatedPricing) {
    if (!car.minimumDailyRate) return false;
    const modelMin = MODEL_MINIMUM_RATES[car.modelName];
    if (modelMin && car.minimumDailyRate < modelMin) return false;
  } else {
    if (!car.dailyRate) return false;
  }
  return true;
}

function getUnlistedCarStatus(car: Car): 'all' | 'partial' | 'none' {
  const populated = UNLISTED_REQUIRED_FIELDS.filter((field) => {
    const val = car[field];
    return val !== null && val !== undefined && val !== '';
  });

  if (populated.length === 0) return 'none';
  if (
    populated.length === UNLISTED_REQUIRED_FIELDS.length &&
    isPricingValid(car)
  ) {
    return 'all';
  }
  return 'partial';
}

const PICKUP_LOCATION_CHARACTER_LIMIT = 40;

export default function Cars() {
  const { styles, theme } = useStyles(stylesheet);
  const local = useLocalSearchParams();
  const status = useAuth.use.status();
  const router = useRouter();
  const [filterText, setFilterText] = useState('');
  const debouncedText = useDebounce(filterText, 400);
  const [showSetupDigitalKeyModal, setShowSetupDigitalKeyModal] =
    useState(false);
  const [showDriverAccountModal, setShowDriverAccountModal] = useState(false);
  const [showAddCarsModal, setShowAddCarsModal] = useState(false);
  const [activeCar, setActiveCar] = useState<Car | null>(null);
  const [showCarListedModal, setShowCarListedModal] = useState(false);
  const [showCarsListedModal, setShowCarsListedModal] = useState(false);
  const [showUnlistCarModal, setShowUnlistCarModal] = useState(false);
  const [unlistedCarsPage, setUnlistedCarsPage] = useState(0);
  const [needsSetupCarsPage, setNeedsSetupCarsPage] = useState(0);
  const [listedCarsPage, setListedCarsPage] = useState(0);
  const [activeTab, setActiveTab] = useState<Tab>(Tab.LISTED);
  const [accordionListedCarsCollapsed, setAccordionListedCarsCollapsed] =
    useState(false);
  const [
    accordionNeedsSetupCarsCollapsed,
    setAccordionNeedsSetupCarsCollapsed,
  ] = useState(false);
  const [accordionUnlistedCarsCollapsed, setAccordionUnlistedCarsCollapsed] =
    useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const trackMetaEvent = useTrackMetaEvent();

  const { data: customer } = useCustomer();

  const { mutateAsync: verifyVirtualKeys, isPending: isVerifyingVirtualKeys } =
    useVerifyVirtualKeys();

  const {
    data: unlistedCarsData,
    isFetchingNextPage: isFetchingNextPageUnlisted,
    fetchNextPage: fetchNextPageUnlisted,
    isLoading: isLoadingUnlisted,
    hasNextPage: hasNextPageUnlisted,
    refetch: refetchUnlistedCars,
    isSuccess: isUnlistedCarsSuccess,
  } = useUnlistedCars({
    text: debouncedText,
  });

  const {
    data: needsSetupCarsData,
    isFetchingNextPage: isFetchingNextPageNeedsSetup,
    fetchNextPage: fetchNextPageNeedsSetup,
    isLoading: isLoadingNeedsSetup,
    hasNextPage: hasNextPageNeedsSetup,
    refetch: refetchNeedsSetupCars,
    isSuccess: isNeedsSetupCarsSuccess,
  } = useNeedsSetupCarsPaginated({
    text: debouncedText,
  });

  const {
    data: listedCarsData,
    isFetchingNextPage: isFetchingNextPageListed,
    fetchNextPage: fetchNextPageListed,
    isLoading: isLoadingListed,
    hasNextPage: hasNextPageListed,
    refetch: refetchListedCars,
    isSuccess: isListedCarsSuccess,
  } = useListedCars({
    text: debouncedText,
  });

  const {
    mutateAsync: connectStandardFleet,
    isPending: isConnectingStandardFleet,
  } = useConnectStandardFleet();

  const { isPolling: isPollingStandardFleet, poll: pollConnectStandardFleet } =
    usePoll<Car[]>(
      () => connectStandardFleet({}),
      (data) => data.length > 0
    );

  const { mutateAsync: relistCar, isPending: isRelistingCar } = useRelistCar();

  const formatPickupLocation = useCallback((address?: string | null) => {
    const value = address?.trim() ?? '';
    const shouldLimitOnSmallDesktop = UnistylesRuntime.breakpoint === 'md';

    if (
      !shouldLimitOnSmallDesktop ||
      value.length <= PICKUP_LOCATION_CHARACTER_LIMIT
    ) {
      return value;
    }

    return `${value.slice(0, PICKUP_LOCATION_CHARACTER_LIMIT)}...`;
  }, []);

  useEffect(() => {
    if (status === 'signIn') {
      refetchUnlistedCars();
      refetchNeedsSetupCars();
      refetchListedCars();
    }
  }, [status]);

  useEffect(() => {
    setListedCarsPage(0);
    setNeedsSetupCarsPage(0);
    setUnlistedCarsPage(0);
  }, [debouncedText]);

  useEffect(() => {
    if (local.showCarsListedModal) {
      setShowCarsListedModal(true);
    }

    if (local.showCarListedModal) {
      setShowCarListedModal(true);
    }
  }, [local]);

  useEffect(() => {
    if (local.fromSf) {
      handleConnectStandardFleet();
    }
  }, [local.fromSf]);

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Partner Cars',
    });
  }, []);

  useEffect(() => {
    if (local.fromSf) {
      mixpanelService.track('Connecting SF Succeeded');
    }
  }, [local.fromSf]);

  const handleConnectStandardFleet = async () => {
    try {
      await pollConnectStandardFleet();

      Toast.show({
        type: 'success',
        text1: 'Cars connected successfully',
      });

      refetchListedCars();
      refetchNeedsSetupCars();
      refetchUnlistedCars();

      // If we arrived here from a reservation's "Reconnect Car" alert, route back.
      if (local.reservationId) {
        router.replace(`/partner/reservations/${local.reservationId}`);
      }
    } catch (error: any) {
      showErrorMessage(error.message);
    }
  };

  const unlistedCars = useMemo(() => {
    return unlistedCarsData?.pages.map((page) => page.data).flat();
  }, [unlistedCarsData]);

  const needsSetupCars = useMemo(() => {
    return needsSetupCarsData?.pages.map((page) => page.data).flat();
  }, [needsSetupCarsData]);

  const listedCars = useMemo(() => {
    return listedCarsData?.pages.map((page) => page.data).flat();
  }, [listedCarsData]);

  const carsNeedVirtualKey = useMemo(() => {
    return listedCars?.filter((car) => {
      return (
        car.isStandardFleetConnected && !car.standardFleetVirtualKeyConnected
      );
    });
  }, [listedCars]);

  const hasActiveSearch = debouncedText.trim().length > 0;
  const hasAnyCars =
    (unlistedCars?.length ?? 0) > 0 ||
    (needsSetupCars?.length ?? 0) > 0 ||
    (listedCars?.length ?? 0) > 0;

  const handleConnectCars = async () => {
    const baseUrl = `${Env.STANDARD_FLEET_URI}&redirect_uri=${Env.STANDARD_FLEET_REDIRECT_URI}&partner_ref=${customer?.partnerToken}`;

    if (Platform.OS === 'web') {
      window.open(baseUrl, '_blank');
    } else {
      await WebBrowser.openBrowserAsync(baseUrl);
    }
  };

  const handleSetupDigitalKey = (car: Car) => {
    setActiveCar(car);
    setShowSetupDigitalKeyModal(true);
  };

  const handleListUnlistedCar = async (car: Car) => {
    const carDetailStatus = getUnlistedCarStatus(car);

    if (carDetailStatus === 'none') {
      router.push({
        pathname: '/partner/cars/setup',
        params: { id: car.id },
      });
    } else if (carDetailStatus === 'partial') {
      router.push(`/partner/cars/${car.id}`);
    } else {
      try {
        await relistCar({ id: car.id });
        setActiveCar(car);
        setShowCarListedModal(true);
        refetchUnlistedCars();
        refetchListedCars();
      } catch (error: any) {
        showErrorMessage(error.message);
      }
    }
  };

  const renderCar = (item: Car, tab: Tab) => {
    const renderCaption = () => {
      if (tab === Tab.LISTED) {
        const renderStatus = () => {
          if (item.isReslaCar || item.isOffline) {
            return (
              <Text type="caption1" style={{ color: theme.colors.success }}>
                Ready
              </Text>
            );
          }

          if (!item.isStandardFleetConnected) {
            return (
              <Text type="caption1" style={{ color: theme.colors.error }}>
                Disconnected
              </Text>
            );
          }

          if (item.standardFleetUserAccessType === 'DRIVER') {
            return (
              <Text type="caption1" style={{ color: theme.colors.warning }}>
                Owner login needed
              </Text>
            );
          }

          if (!item.standardFleetVirtualKeyConnected) {
            return (
              <Text type="caption1" style={{ color: theme.colors.warning }}>
                Add Digital Key
              </Text>
            );
          }

          return (
            <Text type="caption1" style={{ color: theme.colors.success }}>
              Ready
            </Text>
          );
        };

        return (
          <View
            style={{
              flexDirection: 'row',
              gap: 4,
              alignItems: 'center',
            }}
          >
            <Text type="caption1">{renderStatus()}</Text>

            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              |
            </Text>

            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              {item.licensePlate}
            </Text>

            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              |
            </Text>

            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              {item.vin}
            </Text>
          </View>
        );
      }

      if (tab === Tab.NEEDS_SETUP) {
        return (
          <View>
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              {item.vin}
            </Text>
          </View>
        );
      }

      if (tab === Tab.UNLISTED) {
        return (
          <View>
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              {item.vin}
            </Text>
          </View>
        );
      }

      return null;
    };

    const renderBottomSection = () => {
      let children;

      if (tab === Tab.LISTED) {
        if (item.isReslaCar || item.isOffline) {
          children = null;
        } else if (!item.isStandardFleetConnected) {
          children = (
            <Button
              type="secondarySmall"
              text="Fix Issue"
              onPress={handleConnectCars}
              style={{ width: '100%', height: 40 }}
            />
          );
        } else if (item.standardFleetUserAccessType === 'DRIVER') {
          children = (
            <Button
              type="secondarySmall"
              text="Fix Issue"
              onPress={() => {
                setActiveCar(item);
                setShowDriverAccountModal(true);
              }}
              style={{ width: '100%', height: 40 }}
            />
          );
        } else if (!item.standardFleetVirtualKeyConnected) {
          children = (
            <Button
              type="secondarySmall"
              text="Fix Issue"
              onPress={async () => {
                if (Platform.OS === 'ios') {
                  const vins = carsNeedVirtualKey
                    ?.map((car) => car.vin)
                    .join(',');

                  await WebBrowser.openBrowserAsync(
                    `https://key-install.standardfleet.com/eon.html?vins=${vins}`
                  );

                  Toast.show({
                    type: 'success',
                    text1: 'Cars might take up to 10 minutes to connect',
                  });

                  verifyVirtualKeys({});
                } else {
                  handleSetupDigitalKey(item);
                }
              }}
              style={{ width: '100%', height: 40 }}
            />
          );
        }
      }

      if (tab === Tab.NEEDS_SETUP) {
        children = (
          <View style={{ gap: 8 }}>
            <Button
              type="secondarySmall"
              text="List Car"
              onPress={() => {
                router.push({
                  pathname: '/partner/cars/setup',
                  params: { id: item.id },
                });
              }}
              style={{ width: '100%', height: 40 }}
            />

            <Button
              type="ghost"
              text="Move To Unlisted"
              onPress={() => {
                setActiveCar(item);
                setShowUnlistCarModal(true);
              }}
              style={{ width: '100%', height: 50 }}
              textStyle={{ color: theme.colors.error, fontSize: 12 }}
            />
          </View>
        );
      }

      if (tab === Tab.UNLISTED) {
        children = (
          <Button
            type="primarySmall"
            text="List Car"
            onPress={() => handleListUnlistedCar(item)}
            loading={isRelistingCar}
            style={{ width: '100%', height: 40 }}
          />
        );
      }

      if (!children) return null;

      return <View style={{ padding: 16 }}>{children}</View>;
    };

    return (
      <Card style={{ gap: 8 }}>
        <Pressable
          onPress={() => {
            router.navigate(`/partner/cars/${item.id}`);
          }}
        >
          <View
            style={{
              borderBottomWidth: renderBottomSection() ? 1 : 0,
              borderBottomColor: theme.colors.card.default.borderColor,
              padding: 16,
              gap: 8,
            }}
          >
            <View>
              <Image
                source={{
                  uri:
                    getCarImages(item.images).length > 0
                      ? getCarImages(item.images)[0].url
                      : '',
                }}
                style={{
                  width: '100%',
                  aspectRatio: 16 / 9,
                }}
                contentFit="cover"
              />
            </View>

            <View>
              {item.vehicleYear && item.brandName && item.modelName ? (
                <Text type="title2">
                  {item.vehicleYear} {item.brandName} {item.modelName}
                </Text>
              ) : (
                <Text type="title2">Loading Car Information...</Text>
              )}
            </View>

            {renderCaption()}
          </View>
        </Pressable>

        {renderBottomSection()}
      </Card>
    );
  };

  const listedCarRows = useCallback(
    (cars: Car[]) => {
      if (!cars) return [];

      const renderChip = (car: Car) => {
        if (car.isReslaCar || car.isOffline) {
          return (
            <Chip
              type={ChipType.SUCCESS}
              icon={<Dot />}
              text={'Ready'}
              iconWidth={8}
              iconHeight={8}
              style={{ alignSelf: 'center' }}
            />
          );
        }

        if (!car.isStandardFleetConnected) {
          return (
            <Chip
              type={ChipType.ERROR}
              icon={<Dot />}
              text={'Disconnected'}
              iconWidth={8}
              iconHeight={8}
              style={{ alignSelf: 'center' }}
            />
          );
        }

        if (car.standardFleetUserAccessType === 'DRIVER') {
          return (
            <Chip
              type={ChipType.WARNING}
              icon={<Dot />}
              text={'Owner login needed'}
              iconWidth={8}
              iconHeight={8}
              style={{ alignSelf: 'center' }}
            />
          );
        }

        if (!car.standardFleetVirtualKeyConnected) {
          return (
            <Chip
              type={ChipType.WARNING}
              icon={<Dot />}
              text={'Add Digital Key'}
              iconWidth={8}
              iconHeight={8}
              style={{ alignSelf: 'center' }}
            />
          );
        }

        return (
          <Chip
            type={ChipType.SUCCESS}
            icon={<Dot />}
            text={'Ready'}
            iconWidth={8}
            iconHeight={8}
            style={{ alignSelf: 'center' }}
          />
        );
      };

      const renderFixIssueButton = (car: Car) => {
        if (car.isReslaCar || car.isOffline) {
          return null;
        }

        if (!car.isStandardFleetConnected) {
          return (
            <Button
              type="secondarySmall"
              text="Fix Issue"
              onPress={handleConnectCars}
              style={{ width: 100, height: 40 }}
            />
          );
        }

        if (car.standardFleetUserAccessType === 'DRIVER') {
          return (
            <Button
              type="secondarySmall"
              text="Fix Issue"
              onPress={() => {
                setActiveCar(car);
                setShowDriverAccountModal(true);
              }}
              style={{ width: 100, height: 40 }}
            />
          );
        }

        if (!car.standardFleetVirtualKeyConnected) {
          return (
            <Button
              type="secondarySmall"
              text="Fix Issue"
              onPress={() => {
                handleSetupDigitalKey(car);
              }}
              style={{ width: 100, height: 40 }}
            />
          );
        }

        return null;
      };

      return cars?.map((car: Car) => ({
        key: car.id,
        id: car.id,
        'car-name': (
          <View style={{ flexDirection: 'row', gap: 16 }}>
            <Image
              source={{
                uri:
                  getCarImages(car.images).length > 0
                    ? getCarImages(car.images)[0]?.url
                    : '',
              }}
              style={{ width: 75, height: 50 }}
              contentFit="contain"
            />

            <View>
              {car.vehicleYear && car.brandName && car.modelName ? (
                <Text type="bodyRegular">
                  {car.vehicleYear} {car.brandName} {car.modelName}
                </Text>
              ) : (
                <Text type="bodyRegular">Loading Car Information...</Text>
              )}

              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                {car.trim?.displayName}
              </Text>
            </View>
          </View>
        ),
        'vin-and-license': (
          <View style={{ gap: 8 }}>
            <Text type="caption1">{car.licensePlate}</Text>

            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              {car.vin}
            </Text>
          </View>
        ),
        'pickup-location': (
          <View style={{ gap: 8 }}>
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              {formatPickupLocation(car.defaultLocation?.address)}
            </Text>
          </View>
        ),
        status: renderChip(car),
        actions: (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {renderFixIssueButton(car)}

            <Button
              type="secondarySmall"
              onPress={() => {
                router.push(`/partner/cars/${car.id}`);
              }}
              rightIcon={
                <Icon
                  type="naked"
                  icon={<Pencil />}
                  fillColor={theme.colors.text.primary}
                  width={12}
                  height={12}
                />
              }
              style={{ width: 50, height: 40 }}
            />
          </View>
        ),
      }));
    },
    [reservations]
  );

  const needsSetupRows = useCallback(
    (cars: Car[]) => {
      if (!cars) return [];

      return cars?.map((car: Car) => ({
        key: car.id,
        id: car.id,
        'car-name': (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
            <Image
              source={{
                uri:
                  getCarImages(car.images).length > 0
                    ? getCarImages(car.images)[0]?.url
                    : '',
              }}
              style={{ width: 75, height: 50 }}
              contentFit="contain"
            />

            <View
              style={{
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {car.vehicleYear && car.brandName && car.modelName ? (
                <Text type="bodyRegular">
                  {car.vehicleYear} {car.brandName} {car.modelName}
                </Text>
              ) : (
                <Text type="bodyRegular">Loading Car Information...</Text>
              )}
            </View>
          </View>
        ),
        vin: (
          <View>
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              {car.vin}
            </Text>
          </View>
        ),
        actions: (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Button
              type="secondarySmall"
              text="List Car"
              onPress={() => {
                router.push({
                  pathname: '/partner/cars/setup',
                  params: { id: car.id },
                });
              }}
              style={{ width: 100, height: 40 }}
            />

            <Button
              type="secondarySmall"
              onPress={() => {
                setActiveCar(car);
                setShowUnlistCarModal(true);
              }}
              rightIcon={
                <Icon
                  type="naked"
                  icon={<TrashFill />}
                  fillColor={theme.colors.text.primary}
                  width={12}
                  height={12}
                />
              }
              style={{ width: 50, height: 40 }}
            />
          </View>
        ),
      }));
    },
    [reservations]
  );

  const unlistedRows = useCallback(
    (cars: Car[]) => {
      if (!cars) return [];

      return cars?.map((car: Car) => ({
        key: car.id,
        id: car.id,
        'car-name': (
          <View style={{ flexDirection: 'row', gap: 16 }}>
            <Image
              source={{
                uri:
                  getCarImages(car.images).length > 0
                    ? getCarImages(car.images)[0]?.url
                    : '',
              }}
              style={{ width: 75, height: 50 }}
              contentFit="contain"
            />

            <View>
              {car.vehicleYear && car.brandName && car.modelName ? (
                <Text type="bodyRegular">
                  {car.vehicleYear} {car.brandName} {car.modelName}
                </Text>
              ) : (
                <Text type="bodyRegular">Loading Car Information...</Text>
              )}

              <Text
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              >
                {car.trim?.displayName}
              </Text>
            </View>
          </View>
        ),
        'vin-and-license': (
          <View style={{ gap: 8 }}>
            <Text type="caption1">{car.licensePlate}</Text>

            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              {car.vin}
            </Text>
          </View>
        ),
        'pickup-location': (
          <View style={{ gap: 8 }}>
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              {formatPickupLocation(car.defaultLocation?.address)}
            </Text>
          </View>
        ),
        actions: (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Button
              type="secondarySmall"
              text="List Car"
              onPress={() => handleListUnlistedCar(car)}
              loading={isRelistingCar}
              style={{ width: 100, height: 40 }}
            />
          </View>
        ),
      }));
    },
    [
      reservations,
      handleListUnlistedCar,
      isRelistingCar,
      formatPickupLocation,
      theme.colors.text.secondary,
    ]
  );

  const renderList = (tab: Tab) => {
    let data: Car[] = [];
    if (tab === Tab.LISTED) {
      data = listedCars || [];
    } else if (tab === Tab.NEEDS_SETUP) {
      data = needsSetupCars || [];
    } else if (tab === Tab.UNLISTED) {
      data = unlistedCars || [];
    }

    let caption = '';
    let hasNextPage = false;
    let isLoadingTab = false;
    let isFetchingNextPage = false;
    let fetchNextPage = () => {};

    if (tab === Tab.LISTED) {
      caption = 'Tap on each vehicle to configure pricing and availability.';
      hasNextPage = hasNextPageListed;
      isLoadingTab = isLoadingListed;
      isFetchingNextPage = isFetchingNextPageListed;
      fetchNextPage = fetchNextPageListed;
    } else if (tab === Tab.NEEDS_SETUP) {
      caption = 'Complete required information to activate these cars.';
      hasNextPage = hasNextPageNeedsSetup;
      isLoadingTab = isLoadingNeedsSetup;
      isFetchingNextPage = isFetchingNextPageNeedsSetup;
      fetchNextPage = fetchNextPageNeedsSetup;
    } else if (tab === Tab.UNLISTED) {
      caption = 'Connected vehicles that are not currently listed.';
      hasNextPage = hasNextPageUnlisted;
      isLoadingTab = isLoadingUnlisted;
      isFetchingNextPage = isFetchingNextPageUnlisted;
      fetchNextPage = fetchNextPageUnlisted;
    }

    if (isLoadingTab && !isFetchingNextPage) {
      return (
        <View
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.background_1,
          }}
        >
          <Loader style={{ height: 250, width: 250 }} />
        </View>
      );
    }

    return (
      <FlashList
        data={data}
        renderItem={({ item }: { item: Car }) => renderCar(item, tab)}
        contentContainerStyle={{
          paddingBottom: 500,
        }}
        ItemSeparatorComponent={() => <View style={{ height: 16 }} />}
        onEndReached={() => {
          if (hasNextPage && !isFetchingNextPage) {
            console.log('end reached');
            fetchNextPage();
          }
        }}
        onRefresh={async () => {
          setRefreshing(true);

          if (tab === Tab.LISTED) {
            await refetchListedCars();
          } else if (tab === Tab.NEEDS_SETUP) {
            await refetchNeedsSetupCars();
          } else if (tab === Tab.UNLISTED) {
            await refetchUnlistedCars();
          }

          setRefreshing(false);
        }}
        refreshing={refreshing}
        estimatedItemSize={20}
        showsVerticalScrollIndicator={false}
        ListHeaderComponent={() => {
          return (
            <View style={{ gap: 8 }}>
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                {caption}
              </Text>

              {activeTab === tab && tab === Tab.NEEDS_SETUP ? (
                <Button
                  text="Bulk List Cars"
                  type="primarySmall"
                  onPress={() => {
                    mixpanelService.track('Button Clicked', {
                      'Button Name': 'Bulk List Cars',
                    });

                    router.replace('/partner/cars/bulk-list');
                  }}
                  style={{ width: 150 }}
                  disabled={needsSetupCars?.length === 0}
                />
              ) : null}
            </View>
          );
        }}
        ListHeaderComponentStyle={{ marginBottom: 16 }}
        ListFooterComponent={() => {
          if (!isFetchingNextPage) return null;

          return <Loader style={{ height: 140 }} />;
        }}
      />
    );
  };

  const renderContent = () => {
    if (isDesktop()) {
      return (
        <ScrollView
          style={styles.container}
          contentContainerStyle={{ flexGrow: 1, paddingBottom: 50, gap: 32 }}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.header}>
            <View style={{ flex: 1 }}>
              <Text type="title1">My Cars</Text>

              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                Manage your fleet and listing status
              </Text>
            </View>

            <View>
              <Button
                text="Connect More Cars"
                type="secondarySmall"
                onPress={() => {
                  mixpanelService.track('Button Clicked', {
                    'Button Name': 'Connect More Cars',
                  });
                  setShowAddCarsModal(true);
                }}
              />
            </View>
          </View>

          <View style={styles.body}>
            <View style={styles.searchInputContainer}>
              <Input
                placeholder="Search for ID, VIN and license"
                value={filterText}
                onChangeText={(t: string) => setFilterText(t)}
                leftIcon={
                  <Icon
                    type="naked"
                    icon={<Search />}
                    fillColor={theme.colors.icon.color}
                    width={22}
                    height={22}
                  />
                }
              />
            </View>

            {carsNeedVirtualKey && carsNeedVirtualKey.length > 0 ? (
              <View>
                <Alert
                  type={AlertType.WARNING}
                  title="Add digital keys to unlock full automation"
                  body="Digital keys give Eon secure access to automate tolls, Supercharger payments, and remote access to your car."
                />
              </View>
            ) : null}
          </View>

          <View style={{ flexGrow: 1 }}>
            <Accordion
              title={`Listed Cars ${
                listedCarsData?.pages[0].meta.totalCount
                  ? `(${listedCarsData?.pages[0].meta.totalCount})`
                  : ''
              }`}
              collapsed={accordionListedCarsCollapsed}
              onToggle={(collapsed) => {
                setAccordionListedCarsCollapsed(collapsed);
              }}
              children={
                <View style={{ gap: 16 }}>
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Click Edit on each vehicle to configure pricing and
                    availability
                  </Text>

                  {isFetchingNextPageListed || isLoadingListed ? (
                    <View
                      style={{
                        width: isDesktop() ? '100%' : undefined,
                        height: isDesktop() ? '100%' : undefined,
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: theme.colors.background_1,
                      }}
                    >
                      <Loader style={{ height: 250, width: 250 }} />
                    </View>
                  ) : (
                    <DataTable
                      columns={[
                        {
                          key: 'car-name',
                          title: 'CAR NAME',
                          style: { minWidth: 280 },
                        },
                        {
                          key: 'vin-and-license',
                          title: 'VIN & LICENSE',
                          style: { minWidth: 180 },
                        },
                        {
                          key: 'pickup-location',
                          title: 'PICKUP LOCATION',
                          style: { minWidth: 220 },
                        },
                        {
                          key: 'status',
                          title: 'STATUS',
                          style: { minWidth: 180 },
                        },
                        {
                          key: 'actions',
                          title: 'ACTIONS',
                          numeric: true,
                          style: { minWidth: 180 },
                        },
                      ]}
                      rows={
                        listedCarsData?.pages[listedCarsPage]
                          ? listedCarRows(
                              listedCarsData?.pages[listedCarsPage].data
                            )
                          : []
                      }
                      page={listedCarsPage}
                      total={listedCarsData?.pages[0].meta.totalCount}
                      rowsPerPage={10}
                      onPageChange={(page: number) => {
                        const currentPage =
                          listedCarsData?.pages.at(-1).meta.page;
                        if (page > currentPage - 1) {
                          fetchNextPageListed();
                        }

                        setListedCarsPage(page);
                      }}
                    />
                  )}
                </View>
              }
            />
          </View>

          <View style={{ flexGrow: 1 }}>
            <Accordion
              title={`Needs Setup ${
                needsSetupCarsData?.pages[0].meta.totalCount
                  ? `(${needsSetupCarsData?.pages[0].meta.totalCount})`
                  : ''
              }`}
              collapsed={accordionNeedsSetupCarsCollapsed}
              onToggle={(collapsed) => {
                setAccordionNeedsSetupCarsCollapsed(collapsed);
              }}
              children={
                <View style={{ gap: 16 }}>
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Complete required information to activate these cars
                  </Text>

                  <View style={{ alignItems: 'flex-end' }}>
                    <Button
                      text="Bulk List Cars"
                      type="primarySmall"
                      onPress={() => {
                        mixpanelService.track('Button Clicked', {
                          'Button Name': 'Bulk List Cars',
                        });

                        router.replace('/partner/cars/bulk-list');
                      }}
                      style={{ width: 150 }}
                      disabled={needsSetupCars?.length === 0}
                    />
                  </View>

                  {isFetchingNextPageNeedsSetup || isLoadingNeedsSetup ? (
                    <View
                      style={{
                        width: '100%',
                        height: '100%',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: theme.colors.background_1,
                      }}
                    >
                      <Loader style={{ height: 250, width: 250 }} />
                    </View>
                  ) : (
                    <DataTable
                      columns={[
                        {
                          key: 'car-name',
                          title: 'CAR NAME',
                          style: { minWidth: 280 },
                        },
                        { key: 'vin', title: 'VIN', style: { minWidth: 150 } },
                        {
                          key: 'actions',
                          title: 'ACTIONS',
                          numeric: true,
                          style: { minWidth: 180 },
                        },
                      ]}
                      rows={
                        needsSetupCarsData?.pages[needsSetupCarsPage]
                          ? needsSetupRows(
                              needsSetupCarsData?.pages[needsSetupCarsPage].data
                            )
                          : []
                      }
                      page={needsSetupCarsPage}
                      total={needsSetupCarsData?.pages[0].meta.totalCount}
                      rowsPerPage={10}
                      onPageChange={(page: number) => {
                        const currentPage =
                          needsSetupCarsData?.pages.at(-1).meta.page;

                        if (page > currentPage - 1) {
                          fetchNextPageNeedsSetup();
                        }

                        setNeedsSetupCarsPage(page);
                      }}
                    />
                  )}
                </View>
              }
            />
          </View>

          <View style={{ flexGrow: 1 }}>
            <Accordion
              title={`Unlisted Cars ${
                unlistedCarsData?.pages[0].meta.totalCount
                  ? `(${unlistedCarsData?.pages[0].meta.totalCount})`
                  : ''
              }`}
              collapsed={accordionUnlistedCarsCollapsed}
              onToggle={(collapsed) => {
                setAccordionUnlistedCarsCollapsed(collapsed);
              }}
              children={
                <View style={{ gap: 16 }}>
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Complete required information to activate these cars
                  </Text>

                  {isFetchingNextPageUnlisted || isLoadingUnlisted ? (
                    <View
                      style={{
                        width: '100%',
                        height: '100%',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: theme.colors.background_1,
                      }}
                    >
                      <Loader style={{ height: 250, width: 250 }} />
                    </View>
                  ) : (
                    <DataTable
                      columns={[
                        {
                          key: 'car-name',
                          title: 'CAR NAME',
                          style: { minWidth: 280 },
                        },
                        {
                          key: 'vin-and-license',
                          title: 'VIN & LICENSE',
                          style: { minWidth: 180 },
                        },
                        {
                          key: 'pickup-location',
                          title: 'PICKUP LOCATION',
                          style: { minWidth: 220 },
                        },
                        {
                          key: 'actions',
                          title: 'ACTIONS',
                          numeric: true,
                          style: { minWidth: 130 },
                        },
                      ]}
                      rows={
                        unlistedCarsData?.pages[unlistedCarsPage]
                          ? unlistedRows(
                              unlistedCarsData?.pages[unlistedCarsPage].data
                            )
                          : []
                      }
                      page={unlistedCarsPage}
                      total={unlistedCarsData?.pages[0].meta.totalCount}
                      rowsPerPage={10}
                      onPageChange={(page: number) => {
                        const currentPage =
                          unlistedCarsData?.pages.at(-1).meta.page;

                        if (page > currentPage - 1) {
                          fetchNextPageUnlisted();
                        }

                        setUnlistedCarsPage(page);
                      }}
                    />
                  )}
                </View>
              }
            />
          </View>
        </ScrollView>
      );
    }

    return (
      <View style={styles.container}>
        <View style={styles.searchInputContainer}>
          <Input
            placeholder="Search for ID, VIN and license"
            value={filterText}
            onChangeText={(t: string) => setFilterText(t)}
            leftIcon={
              <Icon
                type="naked"
                icon={<Search />}
                fillColor={theme.colors.icon.color}
                width={22}
                height={22}
              />
            }
          />
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{
            maxHeight: 50,
            width: '100%',
            padding: 16,
          }}
          contentContainerStyle={{
            alignItems: 'center',
            flexDirection: 'row',
            gap: 8,
            paddingRight: 50,
          }}
        >
          <Button
            text="Listed"
            type={activeTab === Tab.LISTED ? 'primarySmall' : 'secondarySmall'}
            onPress={() => {
              setActiveTab(Tab.LISTED);
            }}
            style={{ width: 90 }}
          />

          <Button
            text="Needs Setup"
            type={
              activeTab === Tab.NEEDS_SETUP ? 'primarySmall' : 'secondarySmall'
            }
            onPress={() => {
              setActiveTab(Tab.NEEDS_SETUP);
            }}
            style={{
              width:
                needsSetupCars &&
                needsSetupCars.length > 0 &&
                activeTab !== Tab.NEEDS_SETUP
                  ? 170
                  : 150,
            }}
            rightIcon={
              needsSetupCars &&
              needsSetupCars.length > 0 &&
              activeTab !== Tab.NEEDS_SETUP ? (
                <View
                  style={{
                    backgroundColor: theme.colors.chip.error.backgroundColor,
                    borderColor: theme.colors.chip.error.border,
                    borderWidth: 1,
                    borderRadius: 100,
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 30,
                    width: 30,
                  }}
                >
                  <Text
                    type="caption1Bold"
                    style={{ color: theme.colors.error }}
                  >
                    {needsSetupCars.length}
                  </Text>
                </View>
              ) : null
            }
          />

          <Button
            text="Unlisted"
            type={
              activeTab === Tab.UNLISTED ? 'primarySmall' : 'secondarySmall'
            }
            onPress={() => {
              setActiveTab(Tab.UNLISTED);
            }}
            style={{ width: 125 }}
          />
        </ScrollView>

        {[Tab.LISTED, Tab.NEEDS_SETUP, Tab.UNLISTED].map((tab) => {
          return (
            <View
              key={tab}
              style={{
                flexGrow: 1,
                display: activeTab === tab ? 'flex' : 'none',
                height: '100vh',
              }}
            >
              {renderList(tab)}
            </View>
          );
        })}
      </View>
    );
  };

  if (isConnectingStandardFleet || isPollingStandardFleet) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  const isCarsDataReady =
    isUnlistedCarsSuccess && isListedCarsSuccess && isNeedsSetupCarsSuccess;

  if (
    isCarsDataReady &&
    !hasAnyCars &&
    !hasActiveSearch &&
    (unlistedCars?.length ?? 0) === 0 &&
    (listedCars?.length ?? 0) === 0 &&
    (needsSetupCars?.length ?? 0) === 0
  ) {
    return (
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.background_1,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <View style={styles.emptyStateContainer}>
          <View>
            <Image
              source={{
                uri: getCarImages([])[0].url,
              }}
              style={{
                width: '100%',
                aspectRatio: 16 / 9,
              }}
              contentFit="cover"
            />
          </View>

          <View style={{ alignItems: 'center', gap: 8 }}>
            <Text type="title2">No Cars Yet</Text>

            <Text
              type="bodyRegular"
              style={[
                { color: theme.colors.text.secondary },
                styles.emptyStateText,
              ]}
            >
              Add your first car to start managing your fleet and listing status
            </Text>
          </View>

          <View>
            <Button
              text="Add Cars"
              type="primary"
              onPress={() => {
                mixpanelService.track('Button Clicked', {
                  'Button Name': 'Add First Car',
                });
                trackMetaEvent('Lead');
                setShowAddCarsModal(true);
              }}
            />
          </View>
        </View>

        <AddCarsModal
          visible={showAddCarsModal}
          onClose={() => setShowAddCarsModal(false)}
          redirectPath="partner/cars/setup?fromSf=1"
        />
      </View>
    );
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
      }}
    >
      {renderContent()}

      <View style={styles.addCarsMobile}>
        <Button
          text="Connect More Cars"
          type="primary"
          onPress={() => {
            mixpanelService.track('Button Clicked', {
              'Button Name': 'Connect More Cars',
            });
            setShowAddCarsModal(true);
          }}
        />
      </View>

      <SetupDigitalKeyModal
        visible={showSetupDigitalKeyModal}
        onClose={() => setShowSetupDigitalKeyModal(false)}
        car={activeCar}
        onVerifyKeySuccess={() => {
          refetchListedCars();
          refetchNeedsSetupCars();
          refetchUnlistedCars();
        }}
      />

      <DriverAccountModal
        visible={showDriverAccountModal}
        onClose={() => setShowDriverAccountModal(false)}
        onReconnect={handleConnectCars}
      />

      <AddCarsModal
        visible={showAddCarsModal}
        onClose={() => setShowAddCarsModal(false)}
        redirectPath="partner/cars?fromSf=1"
      />

      <CarListedModal
        visible={showCarListedModal}
        onClose={() => {
          router.setParams({ showCarListedModal: undefined });
          setShowCarListedModal(false);
        }}
        carId={
          activeCar?.id?.toString() ?? (local.showCarListedModalCarId as string)
        }
      />

      <CarsListedModal
        visible={showCarsListedModal}
        onClose={() => {
          router.setParams({ showCarsListedModal: undefined });
          setShowCarsListedModal(false);
        }}
      />

      <UnlistCarModal
        visible={showUnlistCarModal}
        car={activeCar}
        onSuccess={() => {
          setShowUnlistCarModal(false);
          Toast.show({
            type: 'success',
            text1: 'Car unlisted successfully',
          });
          refetchListedCars();
          refetchNeedsSetupCars();
          refetchUnlistedCars();
        }}
        onClose={() => {
          setShowUnlistCarModal(false);
        }}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  emptyStateText: {
    textAlign: 'center',
    width: {
      xs: undefined,
      md: 620,
    },
    paddingHorizontal: {
      xs: 40,
      md: 0,
    },
  },
  emptyStateContainer: {
    flexGrow: 1,
    gap: 16,
    padding: 16,
    justifyContent: 'center',
    width: {
      xs: 'auto',
      md: 400,
    },
  },
  container: {
    flexGrow: 1,
    paddingTop: {
      xs: 16,
      md: 56,
    },
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingBottom: {
      xs: 24,
      md: 56,
    },
    gap: {
      xs: 8,
      md: 64,
    },
  },
  header: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  body: {
    flexGrow: 1,
    gap: {
      xs: 56,
      md: 32,
    },
  },
  searchInputContainer: {
    width: {
      xs: '100%',
      md: 325,
    },
  },
  addCarsMobile: {
    width: '100%',
    padding: 16,
    display: {
      xs: 'flex',
      md: 'none',
    },
    position: {
      xs: 'absolute',
    },
    bottom: {
      xs: 0,
    },
    flexDirection: 'row',
  },
}));
